import { ApiError, api } from './api.js';
import { isConvertible } from './asset-import.js';
import { setControllerTextMode } from '../components/animator/text-mode.js';
import { CONTROLLER_EXT } from '../components/animator/controller-doc.js';
import { logEditor } from '../state/console.js';
import { useModel } from '../state/model.js';
import { usePixel } from '../state/pixel.js';
import { useSound } from '../state/sound.js';
import { useGraph } from '../state/graph.js';
import { useProcgen } from '../state/procgen.js';
import { useLayout } from '../state/layout.js';
import { useVideo } from '../state/video.js';
import { assetKind, useProjectFiles } from '../state/project-files.js';
import { useScene } from '../state/scene.js';
import { useWorkspace } from '../state/workspace.js';

/** Open a project file in the tab that edits it best. */
export function openAsset(path: string, options: { asText?: boolean } = {}): void {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const kind = assetKind({ name, kind: 'file' });
  const ws = useWorkspace.getState();
  if (!options.asText && /\.procgen\.json$/i.test(name)) {
    void useProcgen.getState().open(path);
    useLayout.getState().show('procgen');
  } else if (!options.asText && /\.(vgraph|shadergraph)\.json$/i.test(name)) {
    void useGraph.getState().open(path);
    useLayout.getState().show('graph');
  } else if (!options.asText && kind === 'scene') {
    void useScene.getState().open(path);
    ws.setView('scene');
  } else if (!options.asText && kind === 'model') {
    void useModel.getState().open(path);
    ws.setView('model');
  } else if (!options.asText && kind === 'pixel') {
    void usePixel.getState().open(path);
    ws.setView('pixel');
  } else if (!options.asText && kind === 'sound') {
    void useSound.getState().open(path);
    ws.setView('sound');
  } else if (!options.asText && kind === 'video') {
    void useVideo.getState().open(path);
    ws.setView('video');
  } else if (!options.asText && /\.glb$/i.test(name)) {
    void editImportedModel(path);
  } else if (!options.asText && isConvertible(name)) {
    void convertSource(path);
  } else {
    // Animator Controllers open in the visual Animator editor (Code tab overlay) unless asked as text.
    if (name.toLowerCase().endsWith(CONTROLLER_EXT)) setControllerTextMode(path, options.asText === true);
    void ws.openFile(path);
    ws.setView('code');
  }
}

/**
 * Opens an imported `.glb` for editing in the Model tab. The first time, it
 * is converted into an editable `<name>.model.json` next to it (the .glb is
 * kept); later it reopens that document.
 */
export async function editImportedModel(path: string, options: { fresh?: boolean } = {}): Promise<string | null> {
  const ws = useWorkspace.getState();
  const projectId = ws.projectId;
  if (!projectId) return null;
  const existing = path.replace(/\.glb$/i, '.model.json');
  try {
    let model = existing;
    const probe = await api.get<{ exists: boolean }>(`/projects/${projectId}/exists?path=${encodeURIComponent(existing)}`);
    if (options.fresh || !probe.exists) {
      logEditor('info', `Converting ${path} into an editable model…`);
      const result = await api.post<{ model: string; notes: string[] }>(`/projects/${projectId}/models/import`, { path });
      model = result.model;
      for (const note of result.notes) logEditor('warn', `${path}: ${note}`);
      logEditor('info', `Created ${model}. Scenes still use ${path} until you point them at the model document.`);
      useProjectFiles.getState().refreshSoon();
    }
    await useModel.getState().open(model);
    ws.setView('model');
    return model;
  } catch (error) {
    logEditor('error', `Could not open ${path} for editing: ${error instanceof ApiError || error instanceof Error ? error.message : String(error)}`);
    return null;
  }
}

/** Converts an FBX / OBJ / USD / Alembic source into a GLB next to it and opens that for editing. */
export async function convertSource(path: string): Promise<string | null> {
  const projectId = useWorkspace.getState().projectId;
  if (!projectId) return null;
  try {
    logEditor('info', `Converting ${path} to a GLB model…`);
    const result = await api.post<{ glb: string; notes: string[] }>(`/projects/${projectId}/models/convert`, { path });
    for (const note of result.notes) logEditor('warn', `${path}: ${note}`);
    logEditor('info', `Created ${result.glb}.`);
    useProjectFiles.getState().refreshSoon();
    return await editImportedModel(result.glb);
  } catch (error) {
    logEditor('error', `Could not convert ${path}: ${error instanceof ApiError || error instanceof Error ? error.message : String(error)}`);
    return null;
  }
}
